/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  getTrafficSourceFromState,
  getUtmReferrerFromState
} from 'modules/meta/selectors'
import {toLower} from 'ramda'
import {createSelector} from 'reselect'

import {isBrowser} from '@vio/core/src/_web/utils/environment'
import {warnServerEnvironment} from '@vio/core/src/_web/utils/logging/server-warning'
import {getBrandHasSeoSrpFlow} from '@vio/core/src/brand/utils'
import {getParsedLabel, parseQueryString} from '@vio/core/src/utils/url'
import {UrlParamsType} from '@vio/core/src/utils/url/types/UrlParams'

import type {Brand} from '@vio/core/src/brand/types'
import type {RootState} from 'store'
import type {SearchParamsType} from 'types/Search'

const DA_TYPE_DEFAULT = 'default'
export const UTM_SOURCE_DEFAULT = 'unknown'

export const getTrafficSource = (
  utmSource: string | null | undefined,
  label: string | null | undefined
): string => {
  const labelParams = new URLSearchParams(label)

  const source = utmSource || labelParams.get('src') || UTM_SOURCE_DEFAULT
  return toLower(source)
}

const getQueryStringObject = createSelector(
  [
    (_state: RootState) => {
      if (!isBrowser) {
        warnServerEnvironment(`getQueryStringObject`)
        return undefined
      }

      return window.location.search
    }
  ],
  queryString => parseQueryString<Record<string, string>>(queryString, true)
)

export const getLandingTrafficSourceFromUrl = createSelector(
  [getQueryStringObject],
  ({label, utm_source}) => getTrafficSource(utm_source, label)
)

export const getLandingUtmReferrerFromUrl = createSelector(
  [getQueryStringObject],
  ({utm_referrer}) => (utm_referrer ? toLower(utm_referrer) : undefined)
)

export const getIsFromTripAdvisor = createSelector(
  [getLandingTrafficSourceFromUrl],
  trafficSource => trafficSource === 'tripadvisor'
)

export const getIsFromGha = createSelector(
  [getTrafficSourceFromState, getUtmReferrerFromState],
  (trafficSource, utmReferrer) =>
    trafficSource === 'gha' || utmReferrer === 'gha'
)

export const getIsFromGVR = createSelector(
  [getTrafficSourceFromState],
  trafficSource => trafficSource === 'gha-vr'
)

export const getIsFromKayak = createSelector(
  [getTrafficSourceFromState],
  trafficSource => trafficSource === 'kayak'
)

export const hasDefaultDates = (urlParameters: {label?: string}): boolean => {
  const searchLabel = urlParameters.label || ''
  const {datype}: {datype?: string} = parseQueryString(searchLabel)

  return Boolean(datype?.toLowerCase() === DA_TYPE_DEFAULT)
}

/**
 * Returns the `wt` (widget type, e.g. `hotelCard`, `multipleHotels`,
 * `searchForm`) value set by widget-generated landing URLs — either as a
 * direct query param or encoded inside the `label` param
 * (e.g. `label=lbl%3Dfoo%26wt%3DmultipleHotels`).
 * @param urlParams - URL param object
 */
export const getWtValueFromLabelParam = (
  urlParams: SearchParamsType | UrlParamsType = {}
): string | undefined => {
  if (!urlParams) return
  // @ts-expect-error - urlParams doesn't technically satisfy Record<string,string>
  return urlParams.wt || getParsedLabel(urlParams).wt
}

interface CheckInParams {
  checkIn?: string
  checkInDistance?: number
}

export const hasDates = (searchParams: CheckInParams): boolean => {
  const {checkIn, checkInDistance} = searchParams

  return Boolean(checkIn || checkInDistance)
}

/**
 * Determines the SRP search flow type based on brand, traffic source, and URL parameter.
 * - Non-Vio brands always return 'regular'
 * - Known traffic sources (ads, emails, etc.) return 'regular'
 * - Otherwise, checks the srpSearchFlow URL parameter (empty = 'seo')
 */
export const getSrpSearchFlow = (
  brand: Brand,
  trafficSource: string,
  srpSearchFlowParam: string | undefined
): 'seo' | 'regular' => {
  if (!getBrandHasSeoSrpFlow(brand)) return 'regular'
  if (trafficSource !== UTM_SOURCE_DEFAULT) return 'regular'
  return srpSearchFlowParam === 'regular' ? 'regular' : 'seo'
}
